import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Spinner } from "../spinner/Spinner";

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "default" | "destructive";
  isLoading?: boolean;
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void;
  children?: React.ReactNode;
}

/**
 * ConfirmDialog - A standardized confirmation dialog with proper async handling.
 * 
 * CRITICAL INVARIANTS:
 * 1. Dialog CANNOT close while async onConfirm is in progress
 * 2. Proper event handling to prevent Radix interference
 * 3. Caller is responsible for closing dialog via onOpenChange after action completes
 * 
 * Features:
 * - Prevents closure during async operations
 * - Handles loading states
 * - Proper event handling to prevent Radix interference
 * - Safe cleanup on unmount
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  variant = "default",
  isLoading = false,
  onConfirm,
  onCancel,
  children,
}) => {
  // Track mounted state to prevent updates after unmount
  const isMountedRef = React.useRef(true);
  
  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Handle open change - block if loading
  const handleOpenChange = React.useCallback((newOpen: boolean) => {
    if (isLoading) {
      // Prevent closing while processing
      return;
    }
    onOpenChange(newOpen);
  }, [isLoading, onOpenChange]);

  // Handle cancel
  const handleCancel = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isLoading) return;
    
    onCancel?.();
    onOpenChange(false);
  }, [isLoading, onCancel, onOpenChange]);

  // Handle confirm with proper event handling
  // CRITICAL: Await the onConfirm action - caller decides when to close
  const handleConfirm = React.useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isLoading) return;
    
    console.log('[ConfirmDialog] Confirm clicked, calling onConfirm');
    
    try {
      await onConfirm();
      console.log('[ConfirmDialog] onConfirm completed successfully');
    } catch (error) {
      console.error("[ConfirmDialog] Error during confirm action:", error);
      throw error; // Re-throw so caller can handle
    }
  }, [isLoading, onConfirm]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        onInteractOutside={(e: { preventDefault: () => void }) => {
          if (isLoading) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={(e: { preventDefault: () => void }) => {
          if (isLoading) {
            e.preventDefault();
          }
        }}
        onPointerDownOutside={(e: { preventDefault: () => void }) => {
          if (isLoading) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && (
            <DialogDescription>{description}</DialogDescription>
          )}
        </DialogHeader>
        
        {children}
        
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
          >
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant={variant === "destructive" ? "destructive" : "default"}
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading && <Spinner size="sm" className="mr-2" />}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

ConfirmDialog.displayName = "ConfirmDialog";
